(() => {
  const ACTIVE_CLASS = "active";
  const SOURCE = "sc-bundles";
  const COLOR_OPTION_NAMES = ["kleur", "farbe", "color", "couleur"];
  const SIZE_OPTION_NAMES = ["size", "größe", "groesse", "maat", "taille", "tamaño", "tamano"];
  const DELIVERY_WRAPPER_SELECTOR = "[data-product-delivery-wrapper]";
  const SC_BUNDLES_ROOT_SELECTOR = "[data-sc-bundles-root]";
  const SC_BUNDLES_LIST_SELECTOR = "[data-sc-bundles-list]";
  const SC_BUNDLES_ITEM_SELECTOR = "[data-sc-bundles-item]";
  const SC_BUNDLES_ITEM_REGION_SELECTORS = [
    "[data-sc-bundles-item-radio]",
    "[data-sc-bundles-item-image]",
    "[data-sc-bundles-badge]",
    "[data-sc-bundles-item-title]",
    "[data-sc-bundles-item-description]",
    "[data-sc-bundles-item-price]"
  ];

  class ScBundles extends HTMLElement {
    connectedCallback() {
      this.boundInit = this.boundInit || this.init.bind(this);
      this.boundOnVariantChanged = this.boundOnVariantChanged || this.onVariantChanged.bind(this);
      this.boundOnClick = this.boundOnClick || this.onClick.bind(this);
      this.boundOnKeydown = this.boundOnKeydown || this.onKeydown.bind(this);
      this.activeProductId = this.dataset.mainProductId || null;
      this.productsById = new Map();
      this.initialized = false;
      this.deliveryRequestId = 0;
      this.bundlesRequestId = 0;

      this.log("connected", {
        formId: this.dataset.formId,
        mainProductId: this.dataset.mainProductId
      });
      this.attachItemListeners();
      this.scheduleInit();
    }

    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener("variant:changed", this.boundOnVariantChanged);
      }

      if (this.deliveryAbortController) {
        this.deliveryAbortController.abort();
      }

      if (this.bundlesAbortController) {
        this.bundlesAbortController.abort();
      }

      this.removeEventListener("click", this.boundOnClick);
      this.removeEventListener("keydown", this.boundOnKeydown);
      document.removeEventListener("DOMContentLoaded", this.boundInit);
      this.initialized = false;
      this.itemListenersAttached = false;
      this.formListenerAttached = false;
    }

    scheduleInit() {
      requestAnimationFrame(() => {
        if (this.init()) {
          return;
        }

        if (document.readyState === "loading") {
          this.log("form not ready, waiting for DOMContentLoaded");
          document.addEventListener("DOMContentLoaded", this.boundInit, { once: true });
        } else {
          this.log("form not ready, retrying after current task");
          setTimeout(this.boundInit, 0);
        }
      });
    }

    init() {
      if (this.initialized || !this.isConnected) {
        return true;
      }

      this.form = document.getElementById(this.dataset.formId);

      if (!this.form) {
        this.log("init skipped: form not found", {
          formId: this.dataset.formId
        });
        return false;
      }

      this.items = Array.from(this.querySelectorAll(SC_BUNDLES_ITEM_SELECTOR));
      this.variantInput = this.form.querySelector("[data-product-form-variant-input]") || this.form.querySelector('input[name="id"]');
      this.productsData = this.readProductsData();

      if (!this.items.length || !this.variantInput || !this.productsData.length) {
        this.log("init skipped: missing required data", {
          items: this.items.length,
          hasVariantInput: Boolean(this.variantInput),
          products: this.productsData.length
        });
        return true;
      }

      this.mainProductId = String(this.dataset.mainProductId || this.productsData[0].id);
      this.mainProduct = this.productsData.find((product) => String(product.id) === this.mainProductId) || this.productsData[0];
      this.mainOptionNames = this.mainProduct.options || [];

      this.productsData.forEach((product) => {
        this.productsById.set(String(product.id), product);
      });

      this.items.forEach((item) => {
        item.dataset.scBundlesInitialVariantId = item.dataset.variantId || "";
      });

      this.attachItemListeners();
      this.attachFormListener();

      const currentVariant = this.findVariantById(this.variantInput.value) || this.getFirstVariant(this.mainProduct);

      if (currentVariant) {
        this.currentOptions = currentVariant.options || [];
        this.log("initial variant resolved", {
          variantId: currentVariant.id,
          options: this.currentOptions
        });
        this.updateItemsForOptions(this.currentOptions, { emit: false, updateBadges: false });
      }

      this.initialized = true;
      this.log("initialized", {
        items: this.items.length,
        products: this.productsData.length,
        activeProductId: this.activeProductId
      });
      return true;
    }

    attachItemListeners() {
      if (this.itemListenersAttached) {
        return;
      }

      this.addEventListener("click", this.boundOnClick);
      this.addEventListener("keydown", this.boundOnKeydown);
      this.itemListenersAttached = true;
    }

    attachFormListener() {
      if (this.formListenerAttached || !this.form) {
        return;
      }

      this.form.addEventListener("variant:changed", this.boundOnVariantChanged);
      this.formListenerAttached = true;
    }

    readProductsData() {
      const script = this.querySelector("[data-sc-bundles-products]");

      if (!script) {
        return [];
      }

      try {
        const parsed = JSON.parse(script.textContent);
        return Array.isArray(parsed.products) ? parsed.products : [];
      } catch (error) {
        this.log("unable to parse product data", error);
        return [];
      }
    }

    onVariantChanged(event) {
      if (event.detail && event.detail.source === SOURCE) {
        this.log("ignored own variant:changed event", event.detail.variant);
        return;
      }

      const variant = event.detail && event.detail.variant;

      if (!variant || !Array.isArray(variant.options)) {
        this.log("ignored variant:changed without options", event.detail);
        return;
      }

      this.currentOptions = variant.options;
      this.log("variant picker changed", {
        variantId: variant.id,
        options: this.currentOptions
      });
      this.renderBundlesForVariant(variant).then((result) => {
        if (result.stale) {
          return;
        }

        this.updateItemsForOptions(this.currentOptions, {
          emit: true,
          sourceVariantId: variant.id,
          mainDeliveryRendered: result.mainDeliveryRendered,
          updateBadges: !result.rendered
        });
      });
    }

    onClick(event) {
      const item = event.target.closest(SC_BUNDLES_ITEM_SELECTOR);

      if (!item || !this.contains(item) || item.hidden) {
        this.log("click ignored", {
          hasItem: Boolean(item),
          itemHidden: Boolean(item && item.hidden),
          initialized: this.initialized
        });
        return;
      }

      if (!this.initialized) {
        this.log("click before init, trying init now", this.getItemLogData(item));
        this.init();
      }

      this.log("item clicked", this.getItemLogData(item));
      this.selectItem(item, { emit: true });
    }

    onKeydown(event) {
      if (event.key !== "Enter" && event.key !== " ") {
        return;
      }

      const item = event.target.closest(SC_BUNDLES_ITEM_SELECTOR);

      if (!item || !this.contains(item) || item.hidden) {
        this.log("keydown ignored", {
          key: event.key,
          hasItem: Boolean(item),
          itemHidden: Boolean(item && item.hidden),
          initialized: this.initialized
        });
        return;
      }

      if (!this.initialized) {
        this.log("keydown before init, trying init now", this.getItemLogData(item));
        this.init();
      }

      event.preventDefault();
      this.log("item selected by keyboard", this.getItemLogData(item));
      this.selectItem(item, { emit: true });
    }

    updateItemsForOptions(options, { emit = true, sourceVariantId = null, mainDeliveryRendered = false, updateBadges = true } = {}) {
      const matches = [];

      this.items.forEach((item) => {
        const product = this.productsById.get(String(item.dataset.productId));
        const variant = product ? this.getVariantForOptions(product, options) : null;
        const isMainProduct = product && String(product.id) === this.mainProductId;
        const shouldShow = Boolean(variant) && (isMainProduct || variant.available);

        item.scBundlesVariant = variant;
        item.hidden = !shouldShow;
        item.toggleAttribute("aria-hidden", !shouldShow);
        item.toggleAttribute("data-sc-bundles-hidden", !shouldShow);
        item.classList.toggle("is-hidden", !shouldShow);
        item.dataset.scBundlesAvailable = variant && variant.available ? "true" : "false";

        if (variant) {
          item.dataset.variantId = String(variant.id);
        }

        matches.push({
          index: item.dataset.scBundlesIndex,
          productId: item.dataset.productId,
          variantId: variant && variant.id,
          available: variant && variant.available,
          visible: shouldShow
        });
      });

      if (updateBadges) {
        this.updateBadges();
      }

      const activeItem = this.getActiveSelectableItem() || this.getFirstSelectableItem();

      this.log("items updated for options", {
        options,
        matches,
        activeProductId: this.activeProductId,
        fallbackProductId: activeItem && activeItem.dataset.productId
      });

      if (activeItem) {
        const activeVariant = activeItem.scBundlesVariant;
        const shouldEmit = emit && String(activeVariant.id) !== String(sourceVariantId);
        const shouldRenderDelivery = !mainDeliveryRendered || String(activeItem.dataset.productId) !== this.mainProductId;

        this.selectItem(activeItem, {
          emit: shouldEmit,
          renderDelivery: shouldRenderDelivery && (Boolean(sourceVariantId) || emit)
        });
      } else {
        this.log("no selectable bundle item found after update", { options });
      }
    }

    updateBadges() {
      let badgeShown = false;

      this.items.forEach((item) => {
        const badge = item.querySelector("[data-sc-bundles-badge]");

        if (!badge) {
          return;
        }

        const shouldShowBadge = !badgeShown && !item.hidden && String(item.dataset.productId) !== this.mainProductId;

        badge.hidden = !shouldShowBadge;
        badge.classList.toggle("is-hidden", !shouldShowBadge);

        if (shouldShowBadge) {
          badgeShown = true;
        }
      });

      this.log("badges updated", {
        badgeShown
      });
    }

    getActiveSelectableItem() {
      return this.items.find((item) => {
        if (item.hidden || !item.scBundlesVariant) {
          return false;
        }

        if (String(item.dataset.productId) !== String(this.activeProductId)) {
          return false;
        }

        return String(item.dataset.productId) === this.mainProductId || item.scBundlesVariant.available;
      });
    }

    getFirstSelectableItem() {
      return this.items.find((item) => !item.hidden && item.scBundlesVariant) || null;
    }

    selectItem(item, { emit = true, renderDelivery = true } = {}) {
      const variant = item.scBundlesVariant || this.findVariantById(item.dataset.variantId);

      if (!variant) {
        this.log("select skipped: no variant for item", this.getItemLogData(item));
        return;
      }

      this.log("selecting item", {
        ...this.getItemLogData(item),
        matchedVariantId: variant.id,
        emit
      });

      this.activeProductId = String(item.dataset.productId);

      this.items.forEach((currentItem) => {
        const isActive = currentItem === item;

        currentItem.dataset.scBundlesActive = isActive ? "true" : "false";
        currentItem.setAttribute("aria-pressed", isActive ? "true" : "false");
        currentItem.classList.toggle(ACTIVE_CLASS, isActive);
      });

      const product = this.productsById.get(String(item.dataset.productId));
      const selectedVariant = this.getVariantWithProductData(variant, product, item);

      this.setFormVariant(item, selectedVariant);

      if (emit) {
        this.dispatchVariantChanged(selectedVariant);
      }

      if (renderDelivery) {
        this.renderDeliveryInfo(product, selectedVariant);
      }
    }

    setFormVariant(item, variant) {
      this.variantInput.value = String(variant.id);
      this.variantInput.dataset.productid = String(item.dataset.productId);
      item.dataset.variantId = String(variant.id);
      this.log("form variant updated", {
        productId: item.dataset.productId,
        variantId: variant.id
      });
    }

    dispatchVariantChanged(variant) {
      this.log("dispatching variant:changed", {
        variantId: variant.id,
        productId: variant.product_id
      });
      this.form.dispatchEvent(new CustomEvent("variant:changed", {
        bubbles: true,
        detail: {
          source: SOURCE,
          variant
        }
      }));
    }

    async renderBundlesForVariant(variant) {
      const sectionId = this.dataset.sectionId;

      if (!sectionId || !this.mainProduct || !variant || !variant.id) {
        this.log("bundles render skipped: missing data", {
          sectionId,
          hasMainProduct: Boolean(this.mainProduct),
          variantId: variant && variant.id
        });

        return { rendered: false, mainDeliveryRendered: false };
      }

      if (this.bundlesAbortController) {
        this.bundlesAbortController.abort();
      }

      const requestId = ++this.bundlesRequestId;
      this.bundlesAbortController = new AbortController();
      const sectionVariant = this.getVariantWithProductData(variant, this.mainProduct, this.getMainItem());
      const url = this.getProductSectionUrl(this.mainProduct, sectionVariant, sectionId);

      this.log("bundles section requested", {
        url: url.toString(),
        variantId: variant.id,
        requestId
      });

      try {
        const container = await this.fetchSectionContainer(url, this.bundlesAbortController.signal);

        if (requestId !== this.bundlesRequestId) {
          this.log("bundles section ignored: stale response", { requestId });
          return { rendered: false, mainDeliveryRendered: false, stale: true };
        }

        const rendered = this.replaceBundlesFromContainer(container);
        const mainDeliveryRendered = this.replaceDeliveryFromContainer(container, {
          productHandle: this.mainProduct.handle,
          variantId: variant.id,
          requestId,
          logPrefix: "bundles section"
        });

        return { rendered, mainDeliveryRendered };
      } catch (error) {
        if (error.name === "AbortError") {
          this.log("bundles section request aborted", { requestId });
          return { rendered: false, mainDeliveryRendered: false, stale: true };
        }

        this.log("bundles section request failed", error);
        return { rendered: false, mainDeliveryRendered: false };
      }
    }

    replaceBundlesFromContainer(container) {
      const currentList = this.querySelector(SC_BUNDLES_LIST_SELECTOR);
      const nextRoot = container.querySelector(SC_BUNDLES_ROOT_SELECTOR);
      const nextList = nextRoot && nextRoot.querySelector(SC_BUNDLES_LIST_SELECTOR);

      if (!currentList || !nextList) {
        this.log("bundles section skipped: list not found", {
          hasCurrentList: Boolean(currentList),
          hasNextList: Boolean(nextList)
        });
        return false;
      }

      const updateStats = this.mergeBundleItems(currentList, nextList);

      this.refreshItems();
      this.log("bundles section replaced", {
        items: this.items.length,
        ...updateStats
      });

      return true;
    }

    mergeBundleItems(currentList, nextList) {
      const currentItemsByKey = new Map();
      const fragment = document.createDocumentFragment();
      const stats = {
        addedItems: 0,
        removedItems: 0,
        updatedAttributes: 0,
        updatedRegions: 0,
        preservedRegions: 0
      };

      Array.from(currentList.querySelectorAll(SC_BUNDLES_ITEM_SELECTOR)).forEach((item) => {
        currentItemsByKey.set(this.getItemMergeKey(item), item);
      });

      Array.from(nextList.querySelectorAll(SC_BUNDLES_ITEM_SELECTOR)).forEach((nextItem) => {
        const key = this.getItemMergeKey(nextItem);
        const currentItem = currentItemsByKey.get(key);

        if (!currentItem) {
          fragment.appendChild(nextItem.cloneNode(true));
          stats.addedItems += 1;
          return;
        }

        currentItemsByKey.delete(key);
        stats.updatedAttributes += this.syncAttributes(currentItem, nextItem);
        this.syncItemRegions(currentItem, nextItem, stats);
        fragment.appendChild(currentItem);
      });

      stats.removedItems = currentItemsByKey.size;
      currentList.replaceChildren(fragment);

      return stats;
    }

    syncItemRegions(currentItem, nextItem, stats) {
      SC_BUNDLES_ITEM_REGION_SELECTORS.forEach((selector) => {
        const currentRegion = currentItem.querySelector(selector);
        const nextRegion = nextItem.querySelector(selector);

        if (!currentRegion && !nextRegion) {
          return;
        }

        if (!currentRegion && nextRegion) {
          currentItem.appendChild(nextRegion.cloneNode(true));
          stats.updatedRegions += 1;
          return;
        }

        if (currentRegion && !nextRegion) {
          currentRegion.remove();
          stats.updatedRegions += 1;
          return;
        }

        if (this.normalizeHtml(currentRegion.outerHTML) === this.normalizeHtml(nextRegion.outerHTML)) {
          stats.preservedRegions += 1;
          return;
        }

        currentRegion.replaceWith(nextRegion.cloneNode(true));
        stats.updatedRegions += 1;
      });
    }

    syncAttributes(currentElement, nextElement) {
      let changedCount = 0;
      const nextAttributeNames = new Set(Array.from(nextElement.attributes).map((attribute) => attribute.name));

      Array.from(currentElement.attributes).forEach((attribute) => {
        if (!nextAttributeNames.has(attribute.name)) {
          currentElement.removeAttribute(attribute.name);
          changedCount += 1;
        }
      });

      Array.from(nextElement.attributes).forEach((attribute) => {
        if (currentElement.getAttribute(attribute.name) !== attribute.value) {
          currentElement.setAttribute(attribute.name, attribute.value);
          changedCount += 1;
        }
      });

      return changedCount;
    }

    getItemMergeKey(item) {
      return `${item.dataset.productId || ""}:${item.dataset.scBundlesIndex || ""}`;
    }

    normalizeHtml(html) {
      return String(html || "").replace(/\s+/g, " ").trim();
    }

    async renderDeliveryInfo(product, variant) {
      const currentWrapper = document.querySelector(DELIVERY_WRAPPER_SELECTOR);
      const sectionId = this.dataset.sectionId;
      const productHandle = variant ? (product && product.handle) || variant.product_handle : null;

      if (!currentWrapper || !sectionId || !productHandle || !variant || !variant.id) {
        this.log("delivery render skipped: missing data", {
          hasWrapper: Boolean(currentWrapper),
          sectionId,
          productHandle,
          variantId: variant && variant.id
        });
        return;
      }

      if (this.deliveryAbortController) {
        this.deliveryAbortController.abort();
      }

      const requestId = ++this.deliveryRequestId;
      this.deliveryAbortController = new AbortController();
      const url = this.getProductSectionUrl(product, variant, sectionId);

      this.log("delivery section requested", {
        url: url.toString(),
        productHandle,
        variantId: variant.id,
        requestId
      });

      try {
        const container = await this.fetchSectionContainer(url, this.deliveryAbortController.signal);

        if (requestId !== this.deliveryRequestId) {
          this.log("delivery section ignored: stale response", { requestId });
          return;
        }

        this.replaceDeliveryFromContainer(container, {
          productHandle,
          variantId: variant.id,
          requestId,
          logPrefix: "delivery section"
        });
      } catch (error) {
        if (error.name === "AbortError") {
          this.log("delivery section request aborted", { requestId });
          return;
        }

        this.log("delivery section request failed", error);
      }
    }

    async fetchSectionContainer(url, signal) {
      const response = await fetch(url.toString(), {
        credentials: "same-origin",
        signal
      });

      if (!response.ok) {
        throw new Error(`Section render failed with status ${response.status}`);
      }

      const html = await response.text();
      const container = document.createElement("div");

      container.innerHTML = html;

      return container;
    }

    replaceDeliveryFromContainer(container, { productHandle, variantId, requestId, logPrefix }) {
      const nextWrapper = container.querySelector(DELIVERY_WRAPPER_SELECTOR);
      const targetWrapper = document.querySelector(DELIVERY_WRAPPER_SELECTOR);

      if (!targetWrapper) {
        this.log(`${logPrefix} skipped: delivery target disappeared`, { requestId });
        return false;
      }

      if (nextWrapper) {
        targetWrapper.replaceWith(nextWrapper);
        this.log(`${logPrefix} delivery replaced`, {
          productHandle,
          variantId,
          requestId
        });
        return true;
      }

      targetWrapper.classList.add("hidden");
      this.log(`${logPrefix} delivery hidden: rendered wrapper not found`, {
        productHandle,
        variantId,
        requestId
      });
      return false;
    }

    getProductSectionUrl(product, variant, sectionId) {
      const productHandle = (product && product.handle) || variant.product_handle;
      const productUrl = (product && product.url) || variant.product_url || `${this.getRootUrlWithoutSlash()}/products/${encodeURIComponent(productHandle)}`;
      const url = new URL(productUrl, window.location.origin);
      const currentView = new URL(window.location.href).searchParams.get("view");

      if (currentView && !url.searchParams.has("view")) {
        url.searchParams.set("view", currentView);
      }

      url.searchParams.set("variant", variant.id);
      url.searchParams.set("section_id", sectionId);

      return url;
    }

    getRootUrlWithoutSlash() {
      if (window.themeVariables && window.themeVariables.routes) {
        return window.themeVariables.routes.rootUrlWithoutSlash || "";
      }

      return "";
    }

    getVariantForOptions(product, selectedOptions) {
      if (!product || !Array.isArray(product.variants) || !Array.isArray(selectedOptions)) {
        return null;
      }

      return product.variants.find((variant) => {
        if (!Array.isArray(variant.options)) {
          return false;
        }

        return variant.options.every((variantOptionValue, productOptionIndex) => {
          const selectedValue = this.getSelectedValueForProductOption(product, productOptionIndex, selectedOptions);

          return selectedValue !== null && variantOptionValue === selectedValue;
        });
      }) || null;
    }

    getSelectedValueForProductOption(product, productOptionIndex, selectedOptions) {
      const productOptions = product.options || [];
      const productOptionName = this.getCanonicalOptionName(productOptions[productOptionIndex]);
      const mainOptionIndex = this.mainOptionNames.findIndex((optionName) => this.getCanonicalOptionName(optionName) === productOptionName);

      if (mainOptionIndex !== -1) {
        return selectedOptions[mainOptionIndex];
      }

      if (productOptions.length === this.mainOptionNames.length) {
        return selectedOptions[productOptionIndex];
      }

      return null;
    }

    normalizeOptionName(value) {
      return String(value || "").trim().toLowerCase();
    }

    getCanonicalOptionName(value) {
      const normalizedValue = this.normalizeOptionName(value);

      if (this.getColorOptionNames().includes(normalizedValue)) {
        return "__color";
      }

      if (this.getSizeOptionNames().includes(normalizedValue)) {
        return "__size";
      }

      return normalizedValue;
    }

    getColorOptionNames() {
      return this.colorOptionNames = this.colorOptionNames || this.getThemeOptionNames("colorLabelList", COLOR_OPTION_NAMES);
    }

    getSizeOptionNames() {
      return this.sizeOptionNames = this.sizeOptionNames || this.getThemeOptionNames("sizeLabelList", SIZE_OPTION_NAMES);
    }

    getThemeOptionNames(key, fallbackNames) {
      const themeNames = window.themeVariables && window.themeVariables.strings && Array.isArray(window.themeVariables.strings[key]) ? window.themeVariables.strings[key] : [];

      return Array.from(new Set([...themeNames, ...fallbackNames].map((name) => this.normalizeOptionName(name)).filter(Boolean)));
    }

    findVariantById(variantId) {
      const normalizedVariantId = String(variantId || "");

      for (const product of this.productsData || []) {
        const variant = (product.variants || []).find((currentVariant) => String(currentVariant.id) === normalizedVariantId);

        if (variant) {
          return variant;
        }
      }

      return null;
    }

    getFirstVariant(product) {
      return product && Array.isArray(product.variants) ? product.variants[0] : null;
    }

    getMainItem() {
      return this.items.find((item) => String(item.dataset.productId) === this.mainProductId) || null;
    }

    refreshItems() {
      this.items = Array.from(this.querySelectorAll(SC_BUNDLES_ITEM_SELECTOR));

      this.items.forEach((item) => {
        item.dataset.scBundlesInitialVariantId = item.dataset.variantId || "";
      });
    }

    getVariantWithProductData(variant, product, item) {
      return {
        ...variant,
        product_id: product && product.id ? product.id : variant.product_id,
        product_handle: product && product.handle ? product.handle : item && item.dataset.productHandle,
        product_url: product && product.url ? product.url : item && item.dataset.productUrl
      };
    }

    getItemLogData(item) {
      return {
        index: item && item.dataset.scBundlesIndex,
        productId: item && item.dataset.productId,
        variantId: item && item.dataset.variantId,
        hidden: Boolean(item && item.hidden),
        active: item && item.dataset.scBundlesActive,
        hasMatchedVariant: Boolean(item && item.scBundlesVariant)
      };
    }

    log(message, data = null) {
      if (data === null) {
        console.log(`[${SOURCE}] ${message}`);
      } else {
        console.log(`[${SOURCE}] ${message}`, data);
      }
    }
  }

  if (!window.customElements.get("sc-bundles")) {
    window.customElements.define("sc-bundles", ScBundles);
  }
})();
